import { defineStore } from 'pinia'
import http from '@/http-common'
import { authStore } from './auth'

const PLAN_SUMMARY_STORAGE_KEY = 'companyPlanSummary'
const PLAN_COMPANY_STORAGE_KEY = 'companyPlanSummaryCompanyId'

export const usePlanStore = defineStore('plan', {
  state: () => ({
    summary: null as any | null,
    loading: false,
    companyId: localStorage.getItem(PLAN_COMPANY_STORAGE_KEY),
  }),

  actions: {
    loadFromStorage() {
      const raw = localStorage.getItem(PLAN_SUMMARY_STORAGE_KEY)
      this.companyId = localStorage.getItem(PLAN_COMPANY_STORAGE_KEY)
      if (!raw) {
        this.summary = null
        return
      }

      try {
        this.summary = JSON.parse(raw)
      } catch {
        this.summary = null
        localStorage.removeItem(PLAN_SUMMARY_STORAGE_KEY)
        localStorage.removeItem(PLAN_COMPANY_STORAGE_KEY)
      }
    },

    persist() {
      if (this.summary && this.companyId) {
        localStorage.setItem(PLAN_SUMMARY_STORAGE_KEY, JSON.stringify(this.summary))
        localStorage.setItem(PLAN_COMPANY_STORAGE_KEY, String(this.companyId))
        return
      }

      localStorage.removeItem(PLAN_SUMMARY_STORAGE_KEY)
      localStorage.removeItem(PLAN_COMPANY_STORAGE_KEY)
    },

    clearPlan() {
      this.summary = null
      this.loading = false
      this.companyId = null
      this.persist()
    },

    async fetchPlanSummary(force = false) {
      const auth = authStore()
      if (!auth.token || !auth.cmpId) {
        this.clearPlan()
        return null
      }

      if (!force && this.summary && String(this.companyId) === String(auth.cmpId)) {
        return this.summary
      }

      this.loading = true

      try {
        const response = await http.get('/company/plan/')
        this.summary = response.data
        this.companyId = String(auth.cmpId)
        this.persist()
        return this.summary
      } catch (error) {
        this.clearPlan()
        throw error
      } finally {
        this.loading = false
      }
    },

    async ensureLoaded() {
      const auth = authStore()
      if (!auth.token || !auth.cmpId) {
        this.clearPlan()
        return null
      }

      if (!this.summary) {
        this.loadFromStorage()
      }

      if (this.summary && String(this.companyId) === String(auth.cmpId)) {
        return this.summary
      }

      return this.fetchPlanSummary()
    },
  },

  getters: {
    modules: (state) => state.summary?.modules || {},
    features: (state) => state.summary?.features || {},
    hasModule:
      (state) =>
      (moduleName: string): boolean => {
        if (!moduleName) return true
        return !!state.summary?.modules?.[moduleName]
      },
    hasFeature:
      (state) =>
      (featureName: string): boolean => {
        if (!featureName) return true
        return !!state.summary?.features?.[featureName]
      },
  },
})
